import React from 'react';
import {
  VStack,
  HStack,
  Text,
  Button,
  Heading,
  Icon,
  useToast,
} from '@chakra-ui/react';
import { FaExclamationTriangle, FaRedo, FaHome, FaWifi, FaServer } from 'react-icons/fa';

interface ErrorDisplayProps {
  type?: 'network' | 'server' | 'notFound' | 'general' | 'validation';
  title?: string;
  message?: string;
  onRetry?: () => void;
  onGoHome?: () => void;
  showRetry?: boolean;
  showGoHome?: boolean;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  type = 'general',
  title,
  message,
  onRetry,
  onGoHome,
  showRetry = true,
  showGoHome = true
}) => {
  const toast = useToast();

  const errorConfig = {
    network: {
      icon: FaWifi,
      title: 'Connection Error',
      message: 'Unable to connect to the server. Please check your internet connection and try again.',
      color: 'orange'
    },
    server: {
      icon: FaServer,
      title: 'Server Error',
      message: 'Something went wrong on our end. Our team has been notified and is working on a fix.',
      color: 'red'
    },
    notFound: {
      icon: FaExclamationTriangle,
      title: 'Page Not Found',
      message: 'The page you\'re looking for doesn\'t exist or has been moved.',
      color: 'yellow'
    },
    validation: {
      icon: FaExclamationTriangle,
      title: 'Validation Error',
      message: 'Please check your input and try again.',
      color: 'orange'
    },
    general: {
      icon: FaExclamationTriangle,
      title: 'Something went wrong',
      message: 'An unexpected error occurred. Please try again.',
      color: 'red'
    }
  };

  const config = errorConfig[type];
  const displayTitle = title || config.title;
  const displayMessage = message || config.message;

  const handleRetry = () => {
    toast({
      title: 'Retrying...',
      status: 'info',
      duration: 1000,
    });
    onRetry?.();
  };

  return (
    <VStack spacing={6} p={8} textAlign="center" maxW="md" mx="auto">
      <Icon
        as={config.icon}
        boxSize={16}
        color={`${config.color}.500`}
      />
      
      <VStack spacing={3}>
        <Heading size="lg" color="gray.800">
          {displayTitle}
        </Heading>
        <Text color="gray.600" fontSize="md" lineHeight="1.6">
          {displayMessage}
        </Text>
      </VStack>

      <HStack spacing={4}>
        {showRetry && onRetry && (
          <Button
            leftIcon={<FaRedo />}
            onClick={handleRetry}
            bg="brand.primary"
            color="black"
            _hover={{ bg: 'brand.primaryHover' }}
          >
            Try Again
          </Button>
        )}
        {showGoHome && onGoHome && (
          <Button
            leftIcon={<FaHome />}
            onClick={onGoHome}
            variant="outline"
            borderColor="brand.green.medium"
            color="brand.green.medium"
            _hover={{ bg: 'brand.green.medium', color: 'white' }}
          >
            Go Home
          </Button>
        )}
      </HStack>

      {type === 'server' && (
        <Text fontSize="sm" color="gray.500">
          Error ID: {Math.random().toString(36).substr(2, 9).toUpperCase()}
        </Text>
      )}
    </VStack>
  );
};

export default ErrorDisplay; 